import axios from "axios";

import { getApiBaseUrl } from "@/config/api";
import { authService } from "@/services/auth";

export interface ContentAsset {
  path: string;
  sha256: string;
  content_type: "image/png" | "video/mp4" | "image/svg+xml";
  url: string;
}

// a2z-Drafter is a research device; flag sets that show it carry a review banner.
export type DeviceId = "a2z-unified-triage" | "a2z-abdo-triage" | "a2z-drafter";

export const deviceLabel: Record<DeviceId, string> = {
  "a2z-unified-triage": "a2z-Unified-Triage",
  "a2z-abdo-triage": "a2z-Abdo-Triage",
  "a2z-drafter": "a2z-Drafter",
};

export interface DeviceCall {
  device: DeviceId;
  run_id: string;
  // Empty means the device did not flag the study; it is not a negative result.
  flagged: string[];
}

export interface ClinicalCase {
  study_uid: string;
  series_uids: string[];
  device: DeviceId;
  finding: string;
  calls: DeviceCall[];
  run_id: string;
  key_slice: string;
  report_excerpt: string;
  source_dicom_uris: string[];
  media_paths: string[];
}

export interface ContentCandidate {
  kind: "case" | "flag_set" | "explainer" | "poll";
  title: string;
  cases: ClinicalCase[];
  posts: Record<string, string>;
  post_order: string[];
  options: string[];
  hold_notice: string;
  review_banner: string;
}

export const formatLabel: Record<ContentCandidate["kind"], string> = {
  case: "Case posts",
  flag_set: "Flag sets",
  explainer: "Explainers",
  poll: "Polls",
};

export interface PermissionInput {
  operation_id: string;
  expected_revision: number;
  status: "pending" | "confirmed" | "declined";
  scope: "public-linkedin";
  evidence_url: string;
  approved_series: string[];
  excluded_series: string[];
  notes: string;
}

export interface PermissionEvent {
  revision: number;
  actor: string;
  recorded_at: string;
  permission: PermissionInput;
}

export interface ContentRecord {
  can_record_permission: boolean;
  candidate_id: string;
  candidate: ContentCandidate;
  revision: number;
  permission: PermissionEvent | null;
  imported_by: string;
  imported_at: string;
  media: ContentAsset[];
}

export interface ContentDetail extends ContentRecord {
  history: PermissionEvent[];
}

const base = () => `${getApiBaseUrl()}/api/admin/content-library`;
const headers = () => {
  const { token } = authService.getStoredAuth();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function listContent(
  cursor: string | null,
  kind: ContentCandidate["kind"] | "all",
): Promise<{
  items: ContentRecord[];
  next_cursor: string | null;
}> {
  const { data } = await axios.get(base(), {
    headers: headers(),
    params: { cursor, ...(kind === "all" ? {} : { kind }) },
  });
  return data;
}

export async function getContent(id: string): Promise<ContentDetail> {
  const { data } = await axios.get(`${base()}/${encodeURIComponent(id)}`, { headers: headers() });
  return data;
}

export async function importContent(file: File): Promise<{ candidate_ids: string[] }> {
  const body = new FormData();
  body.append("file", file);
  const { data } = await axios.post(`${base()}/imports`, body, { headers: headers() });
  return data;
}

export async function savePermission(
  id: string,
  change: PermissionInput,
): Promise<PermissionEvent> {
  const { data } = await axios.post(`${base()}/${encodeURIComponent(id)}/permission`, change, {
    headers: headers(),
  });
  return data;
}

export function contentError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  return error instanceof Error ? error.message : "The request failed. Please try again.";
}

export function permissionLabel(record: ContentRecord): string {
  if (!record.candidate.cases.length) return "Final wording review needed";
  switch (record.permission?.permission.status) {
    case "confirmed":
      return "Supplier permission confirmed";
    case "declined":
      return "Supplier permission declined";
    default:
      return "Supplier permission pending";
  }
}
